import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import HomePage from './components/pages/Home';
import AboutPage from './components/pages/About';
import ProjectsPage from './components/pages/Projects';
import ContactPage from './components/pages/Contact';
import ExperiencePage from './components/pages/Experience';
import BeyondCodePage from './components/pages/BeyondCode';
import CaseStudiesPage from './components/pages/CaseStudies';
import CaseStudyDetail from './components/pages/CaseStudyDetail';
import useIsMobile from './hooks/useIsMobile';
import { colors, font, motion } from './styles/tokens';

// One list drives both the nav and the routes so they can't drift apart.
const NAV = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/projects', label: 'Projects' },
  { to: '/case-studies', label: 'Case Studies' },
  { to: '/experience', label: 'Experience' },
  { to: '/contact', label: 'Contact' },
  { to: '/beyond-code', label: 'Beyond Code' }
];

// Every route change starts at the top of the page.
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const App = () => {
  const [navHovered, setNavHovered] = useState(false);
  const [navClicked, setNavClicked] = useState(false);
  const isMobile = useIsMobile();
  const { pathname } = useLocation();

  // The inverted-text layer is a second copy of the page pinned to the
  // viewport. Scroll is synced by writing transform straight to the node so
  // scrolling never triggers a React re-render of the duplicated tree.
  const invertLayerRef = useRef(null);
  const invertInnerRef = useRef(null);

  useEffect(() => {
    const sync = () => {
      if (invertInnerRef.current) {
        invertInnerRef.current.style.transform = `translateY(${-window.scrollY}px)`;
      }
      if (invertLayerRef.current) {
        // match the body's width exactly, scrollbar excluded, so the clone
        // lines up with the real text character for character
        invertLayerRef.current.style.width = `${document.documentElement.clientWidth}px`;
      }
    };

    sync();
    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    return () => {
      window.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isMobile && navClicked) {
        const navElement = document.getElementById('nav-container');
        if (navElement && !navElement.contains(e.target)) {
          setNavClicked(false);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMobile, navClicked]);

  const handleNavClick = (e) => {
    if (isMobile) {
      e.stopPropagation();
      setNavClicked(!navClicked);
    }
  };

  const navOpen = navHovered || navClicked;
  const navSize = isMobile ? 340 : 400;
  const navCollapsed = isMobile ? 104 : 120;
  const navRadius = navOpen ? navSize : navCollapsed;
  const navStep = isMobile ? 30 : 35;
  const navStart = isMobile ? 62 : 70;

  // The nav is a quarter disc centred on the viewport's top-left corner, so
  // its radius is also the clip radius for the inverted layer.
  const circleShape = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: `${navRadius}px`,
    height: `${navRadius}px`,
    borderRadius: '0 0 100% 0',
    transition: `width ${motion.slow} ${motion.ease}, height ${motion.slow} ${motion.ease}`
  };

  const clip = `circle(${navRadius}px at 0px 0px)`;

  // The clone is a desktop flourish. On mobile the disc is a real menu and
  // page text bleeding through it just makes the labels unreadable.
  const showInvert = !isMobile;

  const styles = {
    app: {
      fontFamily: font.body,
      background: colors.bg,
      color: colors.ink,
      minHeight: '100vh',
      fontSize: '18px',
      lineHeight: 1.7,
      position: 'relative'
    },
    // layer 1 — the sage shape itself
    navCircle: {
      ...circleShape,
      background: colors.sage,
      zIndex: 1000,
      pointerEvents: 'none'
    },
    // layer 2 — the page again, in cream, clipped to the disc
    invertLayer: {
      position: 'fixed',
      top: 0,
      left: 0,
      height: '100vh',
      overflow: 'hidden',
      zIndex: 1001,
      pointerEvents: 'none',
      userSelect: 'none',
      clipPath: clip,
      WebkitClipPath: clip,
      transition: `clip-path ${motion.slow} ${motion.ease}, -webkit-clip-path ${motion.slow} ${motion.ease}`
    },
    // layer 3 — sage backing behind the labels. Same colour as the disc, so
    // it is invisible; it exists only to stop cloned text running under the
    // menu on narrower windows where the content column reaches that far left.
    navLabelMask: {
      ...circleShape,
      zIndex: 1002,
      pointerEvents: 'none',
      clipPath: clip,
      WebkitClipPath: clip,
      // must grow on the same curve as the disc, or the clip snaps open and
      // flashes the bare sage rectangle before the disc catches up
      transition: `${circleShape.transition}, clip-path ${motion.slow} ${motion.ease}, -webkit-clip-path ${motion.slow} ${motion.ease}`
    },
    // layer 4 — transparent hit area with the labels, above everything
    navContainer: {
      ...circleShape,
      zIndex: 1003,
      cursor: 'pointer'
    },
    navIcon: {
      position: 'absolute',
      top: '25px',
      left: '25px',
      color: colors.bg,
      fontSize: '22px',
      fontWeight: 300,
      transition: `opacity ${motion.fast} ease`,
      opacity: navOpen ? 0 : 1
    },
    navMenu: {
      position: 'absolute',
      top: 0,
      left: 0,
      opacity: navOpen ? 1 : 0,
      pointerEvents: navOpen ? 'all' : 'none',
      transition: `opacity ${motion.slow} ${motion.ease}`
    },
    navItem: {
      position: 'absolute',
      color: colors.bg,
      textDecoration: 'none',
      fontSize: isMobile ? '15px' : '16px',
      fontFamily: font.mono,
      transition: `color ${motion.fast} ease, transform ${motion.fast} ease`,
      cursor: 'pointer',
      padding: '5px 10px 5px 0',
      whiteSpace: 'nowrap',
      textAlign: 'left',
      display: 'flex',
      alignItems: 'center'
    }
  };

  const NavItem = ({ to, children, index }) => {
    const [hovered, setHovered] = useState(false);
    const active = to === '/' ? pathname === '/' : pathname.startsWith(to);

    return (
      <Link
        to={to}
        style={{
          ...styles.navItem,
          top: `${navStart + index * navStep}px`,
          left: '22px',
          color: hovered && !isMobile ? colors.inkStrong : colors.bg,
          transform: hovered && !isMobile ? 'translateX(3px)' : 'translateX(0)'
        }}
        onMouseEnter={() => !isMobile && setHovered(true)}
        onMouseLeave={() => !isMobile && setHovered(false)}
        onClick={() => isMobile && setNavClicked(false)}
      >
        {/* fixed-width marker so the label never shifts */}
        <span
          style={{
            display: 'inline-block',
            width: '16px',
            flexShrink: 0,
            fontSize: '10px',
            opacity: active ? 1 : 0,
            transition: `opacity ${motion.fast} ease`
          }}
        >
          ✦
        </span>
        {children}
      </Link>
    );
  };

  const pageContent = (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/projects" element={<ProjectsPage />} />
      <Route path="/case-studies" element={<CaseStudiesPage />} />
      <Route path="/case-studies/:id" element={<CaseStudyDetail />} />
      <Route path="/experience" element={<ExperiencePage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/beyond-code" element={<BeyondCodePage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );

  return (
    <div style={styles.app}>
      <ScrollToTop />
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }

          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          body {
            margin: 0;
            background: ${colors.bg};
            color: ${colors.ink};
            font-family: ${font.body};
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
          }

          /* Headings keep the mono voice; prose does not. */
          h1, h2, h3, h4, h5, h6 {
            font-family: ${font.mono};
          }

          button {
            font-family: inherit;
          }

          em {
            font-style: italic;
            color: ${colors.sageDeep};
          }

          a {
            color: inherit;
          }

          ::selection {
            background: ${colors.highlight};
          }

          /* The clone paints glyphs only — every surface, rule, shadow and
             image is stripped so the sage disc shows through behind the text. */
          .nav-invert,
          .nav-invert * {
            background: none !important;
            background-color: transparent !important;
            background-image: none !important;
            border-color: transparent !important;
            box-shadow: none !important;
            color: ${colors.bg} !important;
            -webkit-text-fill-color: ${colors.bg} !important;
            animation: none !important;
          }

          .nav-invert img,
          .nav-invert svg,
          .nav-invert video,
          .nav-invert canvas {
            visibility: hidden !important;
          }
        `}
      </style>

      {/* 1. the sage quarter disc */}
      <div style={styles.navCircle} />

      {/* 2. the page again in cream, clipped to the disc */}
      {showInvert && (
        <div
          ref={invertLayerRef}
          className="nav-invert"
          style={styles.invertLayer}
          aria-hidden="true"
          role="presentation"
        >
          <div ref={invertInnerRef} style={{ willChange: 'transform' }}>
            {pageContent}
          </div>
        </div>
      )}

      {/* 3. keeps the label column solid */}
      {showInvert && (
        <div style={styles.navLabelMask} aria-hidden="true">
          <div
            style={{
              position: 'absolute',
              left: '40px',
              top: '88px',
              width: '184px',
              height: `${navStart + 6 * navStep + 20}px`,
              background: colors.sage
            }}
          />
        </div>
      )}

      {/* 4. transparent hit area carrying the labels, above everything */}
      <div
        id="nav-container"
        style={styles.navContainer}
        onMouseEnter={() => !isMobile && setNavHovered(true)}
        onMouseLeave={() => !isMobile && setNavHovered(false)}
        onClick={handleNavClick}
      >
        <div style={styles.navIcon}>☰</div>
        <nav style={styles.navMenu}>
          {NAV.map((item, i) => (
            <NavItem key={item.to} to={item.to} index={i}>
              {item.label}
            </NavItem>
          ))}
        </nav>
      </div>

      {pageContent}
    </div>
  );
};

export default App;
